// Decision-makers answer the tickets assigned to them. They see the question,
// the member reports, and the decision thread; nothing else on the desk.
import { useCallback, useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Gavel } from 'lucide-react';
import toast from 'react-hot-toast';
import { ticketsAPI } from '../api/client';
import { useAuth } from '../context/useAuth';
import { ScreenshotStrip } from '../components/TicketScreenshots';
import type { DecisionTicket } from '../types';

function moment(value: string) {
  return new Date(value).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export default function Decisions() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState<DecisionTicket[] | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [decision, setDecision] = useState('');
  const [busy, setBusy] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(() => {
    ticketsAPI.myDecisions()
      .then(setTickets)
      .catch(() => {
        setTickets([]);
        toast.error('Could not load your decisions.');
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const linked = searchParams.get('ticket');
  useEffect(() => {
    if (!linked) return;
    setOpenId(Number(linked));
    setSearchParams((params) => {
      params.delete('ticket');
      return params;
    }, { replace: true });
  }, [linked, setSearchParams]);

  if (user && !user.is_decision_maker) {
    return <Navigate to="/" replace />;
  }

  const replace = (updated: DecisionTicket) => {
    setTickets((rows) => (rows || []).map((row) => (row.id === updated.id ? updated : row)));
  };

  const send = async (kind: 'comment' | 'decide', ticketId: number) => {
    const text = (kind === 'comment' ? comment : decision).trim();
    if (!text) return;
    setBusy(true);
    try {
      const updated = kind === 'comment'
        ? await ticketsAPI.decisionComment(ticketId, text)
        : await ticketsAPI.decide(ticketId, text);
      replace(updated);
      if (kind === 'comment') setComment('');
      else {
        setDecision('');
        toast.success('Decision recorded.');
      }
    } catch (err) {
      const serverError = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      toast.error(serverError || 'Could not save that.');
    } finally {
      setBusy(false);
    }
  };

  const current = tickets?.find((row) => row.id === openId) || null;

  if (current) {
    return (
      <div className="max-w-2xl mx-auto space-y-3">
        <button
          type="button"
          onClick={() => { setOpenId(null); load(); }}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-navy transition"
        >
          <ArrowLeft size={16} />
          All decisions
        </button>
        <div className="bg-white rounded-2xl p-5 shadow-sm space-y-3">
          <p className="text-xs text-gray-400">{current.kind_display} · asked {current.decision_requested_at ? moment(current.decision_requested_at) : ''}</p>
          <h1 className="text-lg font-bold text-navy">{current.title}</h1>
          {current.decision_question && (
            <div className="rounded-xl bg-purple-50 p-3">
              <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide">The question</p>
              <p className="text-sm text-gray-800 whitespace-pre-wrap mt-1">{current.decision_question}</p>
            </div>
          )}
        </div>

        <details className="bg-white rounded-2xl px-5 py-3 shadow-sm">
          <summary className="cursor-pointer font-semibold text-navy text-sm">
            What members said ({current.reports.length})
          </summary>
          <div className="mt-3 space-y-3">
            {current.reports.map((report) => (
              <div key={report.id} className="border border-gray-100 rounded-xl p-3">
                <p className="text-xs text-gray-400">{report.user_name} · {moment(report.created_at)}</p>
                {report.message && <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{report.message}</p>}
                {(report.images || []).length > 0 && (
                  <div className="mt-2"><ScreenshotStrip images={report.images} /></div>
                )}
              </div>
            ))}
          </div>
        </details>

        <div className="bg-white rounded-2xl p-5 shadow-sm space-y-3">
          <h2 className="font-semibold text-navy text-sm">Discussion</h2>
          {current.thread.map((message) => (
            <div key={message.id} className="rounded-xl bg-gray-50 p-3">
              <p className="text-xs font-semibold text-gray-500">
                {message.author_name} <span className="font-normal text-gray-400">· {moment(message.created_at)}</span>
              </p>
              {message.body && <p className="text-sm text-gray-800 mt-1 whitespace-pre-wrap">{message.body}</p>}
              {(message.images || []).length > 0 && (
                <div className="mt-2"><ScreenshotStrip images={message.images} /></div>
              )}
            </div>
          ))}
          <textarea
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            rows={2}
            placeholder="Ask a question or add context for the team"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:outline-none"
          />
          <button
            type="button"
            disabled={busy || !comment.trim()}
            onClick={() => void send('comment', current.id)}
            className="px-4 py-2 border border-navy text-navy rounded-lg text-sm font-semibold hover:bg-navy hover:text-white transition disabled:opacity-50"
          >
            Post comment
          </button>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm border-l-4 border-purple-300 space-y-3">
          <h2 className="font-semibold text-navy text-sm flex items-center gap-2">
            <Gavel size={16} className="text-purple-700" />
            Your decision
          </h2>
          {current.decisions.map((entry) => (
            <div key={entry.id} className="rounded-xl bg-purple-50 p-3">
              <p className="text-xs text-gray-500">{entry.decided_by_name} · {moment(entry.created_at)}</p>
              <p className="text-sm text-gray-800 whitespace-pre-wrap mt-0.5">{entry.decision}</p>
            </div>
          ))}
          <p className="text-xs text-gray-500">
            Saved to the decisions log with today's date, so the team builds from it.
          </p>
          <textarea
            value={decision}
            onChange={(event) => setDecision(event.target.value)}
            rows={3}
            placeholder={current.decisions.length ? 'Change or add to the decision' : 'Write the decision'}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:outline-none"
          />
          <button
            type="button"
            disabled={busy || !decision.trim()}
            onClick={() => void send('decide', current.id)}
            className="px-4 py-2 bg-purple-700 text-white rounded-lg text-sm font-semibold hover:bg-purple-800 transition disabled:opacity-50"
          >
            Record decision
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-navy">Decisions</h1>
        <p className="text-gray-600 mt-1 text-sm">
          Product questions the team needs you to answer. Please answer within two days.
        </p>
      </div>
      {tickets === null && <p className="text-sm text-gray-400">Loading…</p>}
      {tickets?.length === 0 && (
        <div className="bg-white rounded-2xl p-8 text-center text-gray-500 shadow-sm">
          Nothing waiting on you.
        </div>
      )}
      {tickets?.map((ticket) => (
        <button
          key={ticket.id}
          type="button"
          onClick={() => { setOpenId(ticket.id); setComment(''); setDecision(''); }}
          className="w-full text-left bg-white rounded-2xl p-4 shadow-sm hover:shadow-md transition"
        >
          <div className="flex items-start justify-between gap-3">
            <p className="min-w-0 font-semibold text-navy text-sm">{ticket.title}</p>
            <span
              className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap shrink-0 ${
                ticket.decision_pending ? 'bg-purple-50 text-purple-700' : 'bg-green-50 text-green-700'
              }`}
            >
              {ticket.decision_pending ? 'Waiting on you' : 'Answered'}
            </span>
          </div>
          {ticket.decision_question && (
            <p className="text-sm text-gray-600 mt-1 line-clamp-2">{ticket.decision_question}</p>
          )}
        </button>
      ))}
    </div>
  );
}
